import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '@/App';
import { useTheme } from '@/contexts/ThemeContext';
import { DefendlyLogo, DefendlyLogoCompact, BSECBadge } from '@/components/DefendlyLogo';
import {
  LayoutDashboard,
  Shield,
  Server,
  Scan,
  FileText,
  LogOut,
  Menu,
  User,
  Settings,
  Activity,
  Globe,
  ChevronDown,
  ChevronRight,
  Building2,
  Users,
  Webhook,
  TrendingUp,
  Crown,
  UserX,
  BarChart3,
  Gauge,
  Radar,
  GitBranch,
  Puzzle,
  Target,
  UserPlus,
  X,
  Sun,
  Moon,
  Calendar,
  Clock,
  CheckCircle,
  FileCheck,
  Bell,
  Crosshair,
  Workflow,
  Link2,
  Bot,
  KeyRound,
  Eye,
  EyeOff,
  Lock,
  Database,
  DollarSign,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

export default function Layout({ children, user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode, toggleTheme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [threatIntelExpanded, setThreatIntelExpanded] = useState(false);
  const [assessmentExpanded, setAssessmentExpanded] = useState(false);
  const [dashboardExpanded, setDashboardExpanded] = useState(false);
  const [itsmExpanded, setItsmExpanded] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({ attackPaths: false });
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [currentOrg, setCurrentOrg] = useState(null);
  const [tenants, setTenants] = useState([]);
  
  // Change Password State
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({ current: '', new: '', confirm: '' });
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Handle Change Password
  const handleChangePassword = async () => {
    if (!passwordData.current || !passwordData.new || !passwordData.confirm) {
      toast.error('Please fill in all fields');
      return;
    }
    if (passwordData.new !== passwordData.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordData.new.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      await api.post('/auth/change-password', {
        current_password: passwordData.current,
        new_password: passwordData.new
      });
      toast.success('Password changed successfully');
      setChangePasswordOpen(false);
      setPasswordData({ current: '', new: '', confirm: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const isActive = (href) => location.pathname === href;
  const isAdmin = user?.role === 'admin' || user?.role === 'platform_admin';
  const isPlatformAdmin = user?.role === 'platform_admin';

  // Handle responsive behavior
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      // Auto-open sidebar on desktop, close on mobile
      if (!mobile && !sidebarOpen) {
        setSidebarOpen(true);
      } else if (mobile && sidebarOpen) {
        setSidebarOpen(false);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close sidebar on mobile when navigating
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isMobile]);

  useEffect(() => {
    fetchCurrentOrg();
    if (isPlatformAdmin) {
      fetchTenants();
    }
  }, [isPlatformAdmin]);

  const fetchCurrentOrg = async () => {
    try {
      const response = await api.get('/organization/current');
      setCurrentOrg(response.data);
    } catch (error) {
      console.error('Failed to fetch organization');
    }
  };

  const fetchTenants = async () => {
    try {
      const response = await api.get('/platform/tenants');
      setTenants(response.data);
    } catch (error) {
      console.error('Failed to fetch tenants');
    }
  };

  return (
    <div className={`min-h-screen flex relative transition-colors duration-300 ${isDarkMode ? 'bg-navy-950' : 'bg-[#F8FAFC]'}`}>
      {/* ========== DARK MODE BACKGROUND ========== */}
      {isDarkMode && (
        <div className="fixed inset-0 z-0 overflow-hidden">
          {/* Base gradient - Deep Navy */}
          <div className="absolute inset-0 bg-gradient-to-br from-navy-950 via-[#0A1429] to-navy-900" />
          
          {/* Accent glow orbs */}
          <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-electric-500/8 rounded-full blur-[120px] animate-pulse-glow" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-electric-600/6 rounded-full blur-[100px] animate-pulse-glow" style={{ animationDelay: '2s' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-teal-500/4 rounded-full blur-[150px] animate-pulse-glow" style={{ animationDelay: '4s' }} />
          
          {/* Network mesh pattern */}
          <div 
            className="absolute inset-0 opacity-40"
            style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, rgba(0, 191, 255, 0.15) 1px, transparent 0)`,
              backgroundSize: '48px 48px'
            }}
          />
          
          {/* Particle effect overlay */}
          <div className="particle-container">
            {[...Array(12)].map((_, i) => (
              <div 
                key={i}
                className="particle"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${100 + Math.random() * 20}%`,
                  animationDelay: `${Math.random() * 15}s`,
                  animationDuration: `${15 + Math.random() * 10}s`
                }}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* ========== LIGHT MODE BACKGROUND ========== */}
      {!isDarkMode && (
        <div className="fixed inset-0 z-0 overflow-hidden">
          {/* Base gradient - Clean off-white */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#F8FAFC] via-[#F1F5F9] to-[#F8FAFC]" />
          
          {/* Subtle accent orbs */}
          <div className="absolute top-0 left-1/4 w-[400px] h-[400px] bg-electric-600/5 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 right-1/4 w-[350px] h-[350px] bg-teal-500/5 rounded-full blur-[80px]" />
          
          {/* Subtle mesh pattern */}
          <div 
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, rgba(0, 165, 224, 0.12) 1px, transparent 0)`,
              backgroundSize: '48px 48px'
            }}
          />
        </div>
      )}
      
      {/* Mobile overlay backdrop */}
      {isMobile && sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      <aside
        className={`fixed left-0 top-0 h-full transition-all duration-300 z-50 ${
          sidebarOpen ? 'w-64 translate-x-0' : 'w-64 -translate-x-full lg:w-0 lg:translate-x-0'
        } overflow-hidden glass-sidebar`}
      >
        <div className="flex flex-col h-full">
          <div className={`p-3 lg:p-4 border-b flex items-center justify-between ${isDarkMode ? 'border-navy-700/50' : 'border-slate-200'}`}>
            {/* Defendly Logo */}
            <img src="/defendly-full-logo.svg" alt="Defendly" className="h-8 w-auto" />
            {/* Close button for mobile */}
            {isMobile && (
              <button 
                onClick={() => setSidebarOpen(false)}
                className={`p-1.5 rounded-lg transition-colors lg:hidden ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-slate-200'}`}
              >
                <X className={`w-5 h-5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} />
              </button>
            )}
          </div>

          <nav className="flex-1 p-2 lg:p-3 space-y-1 overflow-y-auto scroll-container">
            {/* Dashboard with sub-menu */}
            <div>
              <button
                onClick={() => setDashboardExpanded(!dashboardExpanded)}
                className={`w-full flex items-center justify-between gap-2.5 px-3 py-2 rounded-lg font-inter text-xs transition-all ${
                  location.pathname === '/dashboard' || location.pathname.startsWith('/dashboard/')
                    ? isDarkMode 
                      ? 'bg-electric-500/15 text-electric-400 border border-electric-500/30'
                      : 'bg-electric-100 text-electric-700 border border-electric-300'
                    : isDarkMode
                      ? 'text-slate-400 hover:bg-white/5 hover:text-white'
                      : 'text-slate-600 hover:bg-slate-200 hover:text-slate-900'
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <LayoutDashboard className="w-4 h-4" />
                  Dashboards
                </div>
                {dashboardExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              </button>
              {dashboardExpanded && (
                <div className="ml-6 mt-1 space-y-1">
                  <button
                    data-testid="nav-dashboard"
                    onClick={() => navigate('/dashboard')}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg font-inter text-xs transition-all ${
                      isActive('/dashboard') 
                        ? isDarkMode ? 'text-electric-400 bg-electric-500/10' : 'text-electric-700 bg-electric-100'
                        : isDarkMode ? 'text-slate-500 hover:text-slate-300' : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <Gauge className="w-3 h-3" />
                    Overview
                  </button>
                  <button
                    data-testid="nav-strategic"
                    onClick={() => navigate('/strategic-dashboard')}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg font-inter text-xs transition-all ${
                      isActive('/strategic-dashboard') 
                        ? isDarkMode ? 'text-electric-400 bg-electric-500/10' : 'text-electric-700 bg-electric-100'
                        : isDarkMode ? 'text-slate-500 hover:text-slate-300' : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <BarChart3 className="w-3 h-3" />
                    Strategic
                  </button>
                  <button
                    data-testid="nav-operational"
                    onClick={() => navigate('/operational-dashboard')}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg font-inter text-xs transition-all ${
                      isActive('/operational-dashboard') 
                        ? isDarkMode ? 'text-electric-400 bg-electric-500/10' : 'text-electric-700 bg-electric-100'
                        : isDarkMode ? 'text-slate-500 hover:text-slate-300' : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <Activity className="w-3 h-3" />
                    Operational
                  </button>
                  <button
                    data-testid="nav-financial-impact"
                    onClick={() => navigate('/financial-impact')}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg font-inter text-xs transition-all ${
                      isActive('/financial-impact') 
                        ? isDarkMode ? 'text-emerald-400 bg-emerald-500/10' : 'text-emerald-700 bg-emerald-100'
                        : isDarkMode ? 'text-slate-500 hover:text-slate-300' : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <DollarSign className="w-3 h-3" />
                    Financial Impact
                  </button>
                </div>
              )}
            </div>

            {/* Threat Intelligence with sub-menu */}
            <div>
              <button
                onClick={() => setThreatIntelExpanded(!threatIntelExpanded)}
                className={`w-full flex items-center justify-between gap-2.5 px-3 py-2 rounded-lg font-inter text-xs transition-all ${
                  location.pathname.startsWith('/threat-intelligence') || location.pathname === '/threats' || 
                  location.pathname === '/exploit-intelligence' || location.pathname.startsWith('/attack-path') ||
                  location.pathname === '/threat-dashboard'
                    ? isDarkMode 
                      ? 'bg-indigo-600/20 text-indigo-400 border border-indigo-500/30'
                      : 'bg-indigo-100 text-indigo-700 border border-indigo-300'
                    : isDarkMode
                      ? 'text-zinc-400 hover:bg-white/5 hover:text-white'
                      : 'text-slate-600 hover:bg-slate-200 hover:text-slate-900'
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <Shield className="w-4 h-4" />
                  Threat Intelligence
                </div>
                {threatIntelExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              </button>
              {threatIntelExpanded && (
                <div className="ml-6 mt-1 space-y-1">
                  <button
                    data-testid="nav-threat-dashboard"
                    onClick={() => navigate('/threat-dashboard')}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg font-inter text-xs transition-all ${
                      isActive('/threat-dashboard')
                        ? isDarkMode ? 'text-indigo-400 bg-indigo-600/10' : 'text-indigo-700 bg-indigo-100'
                        : isDarkMode ? 'text-zinc-500 hover:text-zinc-300' : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    Global Threat Map
                  </button>
                  <button
                    data-testid="nav-threat-feeds"
                    onClick={() => navigate('/threat-intelligence')}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg font-inter text-xs transition-all ${
                      isActive('/threat-intelligence')
                        ? isDarkMode ? 'text-indigo-400 bg-indigo-600/10' : 'text-indigo-700 bg-indigo-100'
                        : isDarkMode ? 'text-zinc-500 hover:text-zinc-300' : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    Threat Intel
                  </button>
                  <button
                    data-testid="nav-threats"
                    onClick={() => navigate('/threats')}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg font-inter text-xs transition-all ${
                      isActive('/threats')
                        ? isDarkMode ? 'text-indigo-400 bg-indigo-600/10' : 'text-indigo-700 bg-indigo-100'
                        : isDarkMode ? 'text-zinc-500 hover:text-zinc-300' : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <Target className="w-3 h-3" />
                    Threat Alerts
                  </button>
                  <button
                    data-testid="nav-threat-hunting"
                    onClick={() => navigate('/threat-hunting-playbooks')}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg font-inter text-xs transition-all ${
                      isActive('/threat-hunting-playbooks')
                        ? isDarkMode ? 'text-orange-400 bg-orange-600/10' : 'text-orange-700 bg-orange-100'
                        : isDarkMode ? 'text-zinc-500 hover:text-zinc-300' : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <Crosshair className="w-3 h-3" />
                    Hunting Playbooks
                  </button>
                  <button
                    data-testid="nav-exploit-intel"
                    onClick={() => navigate('/exploit-intelligence')}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg font-inter text-xs transition-all ${
                      isActive('/exploit-intelligence')
                        ? isDarkMode ? 'text-indigo-400 bg-indigo-600/10' : 'text-indigo-700 bg-indigo-100'
                        : isDarkMode ? 'text-zinc-500 hover:text-zinc-300' : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <TrendingUp className="w-3 h-3" />
                    Exploit Intel
                  </button>
                  <button
                    data-testid="nav-attack-paths"
                    onClick={() => navigate('/attack-paths')}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg font-inter text-xs transition-all ${
                      isActive('/attack-paths')
                        ? isDarkMode ? 'text-indigo-400 bg-indigo-600/10' : 'text-indigo-700 bg-indigo-100'
                        : isDarkMode ? 'text-zinc-500 hover:text-zinc-300' : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <GitBranch className="w-3 h-3" />
                    Attack Paths
                  </button>
                </div>
              )}
            </div>

            {/* Asset Management (Unified) */}
            <button
              data-testid="nav-asset-management"
              onClick={() => navigate('/asset-management')}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg font-inter text-xs transition-all ${
                isActive('/asset-management') || isActive('/assets') || isActive('/asset-discovery') || isActive('/easm')
                  ? isDarkMode 
                    ? 'bg-indigo-600/20 text-indigo-400 border border-indigo-500/30 shadow-neon-indigo'
                    : 'bg-indigo-100 text-indigo-700 border border-indigo-300'
                  : isDarkMode
                    ? 'text-zinc-400 hover:bg-white/5 hover:text-white'
                    : 'text-slate-600 hover:bg-slate-200 hover:text-slate-900'
              }`}
            >
              <Server className="w-4 h-4" />
              Asset Management
            </button>

            {/* Assessment Center with sub-menu - TVM Mirror Structure */}
            <div>
              <button
                onClick={() => setAssessmentExpanded(!assessmentExpanded)}
                className={`w-full flex items-center justify-between gap-2.5 px-3 py-2 rounded-lg font-inter text-xs transition-all ${
                  location.pathname.startsWith('/assessment') || location.pathname === '/compliance' || location.pathname === '/secureprobe'
                    ? isDarkMode 
                      ? 'bg-indigo-600/20 text-indigo-400 border border-indigo-500/30'
                      : 'bg-indigo-100 text-indigo-700 border border-indigo-300'
                    : isDarkMode
                      ? 'text-zinc-400 hover:bg-white/5 hover:text-white'
                      : 'text-slate-600 hover:bg-slate-200 hover:text-slate-900'
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <Scan className="w-4 h-4" />
                  Assessment Center
                </div>
                {assessmentExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              </button>
              {assessmentExpanded && (
                <div className="ml-6 mt-1 space-y-1">
                  <button
                    data-testid="nav-sbom"
                    onClick={() => navigate('/assessment-center?tab=sbom')}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg font-inter text-xs transition-all ${
                      location.pathname === '/assessment-center'
                        ? isDarkMode ? 'text-indigo-400 bg-indigo-600/10' : 'text-indigo-700 bg-indigo-100'
                        : isDarkMode ? 'text-zinc-500 hover:text-zinc-300' : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <Shield className="w-3 h-3" />
                    SBOM Management
                  </button>
                  <button
                    data-testid="nav-secureprobe"
                    onClick={() => navigate('/secureprobe')}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg font-inter text-xs transition-all ${
                      isActive('/secureprobe')
                        ? isDarkMode ? 'text-red-400 bg-red-600/10' : 'text-red-700 bg-red-100'
                        : isDarkMode ? 'text-zinc-500 hover:text-zinc-300' : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <Target className="w-3 h-3" />
                    SecureProbe
                  </button>
                  <button
                    data-testid="nav-compliance"
                    onClick={() => navigate('/compliance')}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg font-inter text-xs transition-all ${
                      isActive('/compliance')
                        ? isDarkMode ? 'text-indigo-400 bg-indigo-600/10' : 'text-indigo-700 bg-indigo-100'
                        : isDarkMode ? 'text-zinc-500 hover:text-zinc-300' : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <FileCheck className="w-3 h-3" />
                    Compliance Checks
                  </button>
                  <button
                    data-testid="nav-compliance-reporting"
                    onClick={() => navigate('/compliance-reporting')}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg font-inter text-xs transition-all ${
                      isActive('/compliance-reporting')
                        ? isDarkMode ? 'text-purple-400 bg-purple-600/10' : 'text-purple-700 bg-purple-100'
                        : isDarkMode ? 'text-zinc-500 hover:text-zinc-300' : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <FileText className="w-3 h-3" />
                    Compliance Reporting
                  </button>
                </div>
              )}
            </div>

            {/* Vulnerability Hub - Direct Link (includes Remediation & Correlation) */}
            <button
              data-testid="nav-vulnerability-hub"
              onClick={() => navigate('/vulnerability-hub')}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg font-inter text-xs transition-all ${
                location.pathname.startsWith('/vulnerability') || isActive('/remediation-factory') || isActive('/correlation-engine')
                  ? isDarkMode 
                    ? 'bg-red-600/20 text-red-400 border border-red-500/30 shadow-neon-red'
                    : 'bg-red-100 text-red-700 border border-red-300'
                  : isDarkMode
                    ? 'text-zinc-400 hover:bg-white/5 hover:text-white'
                    : 'text-slate-600 hover:bg-slate-200 hover:text-slate-900'
              }`}
            >
              <Shield className="w-4 h-4" />
              Vulnerability Hub
            </button>

            {/* BAS Engine - Breach and Attack Simulation */}
            <button
              data-testid="nav-bas-engine"
              onClick={() => navigate('/bas-engine')}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg font-inter text-xs transition-all ${
                isActive('/bas-engine')
                  ? isDarkMode 
                    ? 'bg-red-600/20 text-red-400 border border-red-500/30 shadow-neon-red'
                    : 'bg-red-100 text-red-700 border border-red-300'
                  : isDarkMode
                    ? 'text-zinc-400 hover:bg-white/5 hover:text-white'
                    : 'text-slate-600 hover:bg-slate-200 hover:text-slate-900'
              }`}
            >
              <Crosshair className="w-4 h-4" />
              BAS Engine
            </button>

            {/* Security Initiatives */}
            <button
              data-testid="nav-security-initiatives"
              onClick={() => navigate('/security-initiatives')}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg font-inter text-xs transition-all ${
                isActive('/security-initiatives')
                  ? isDarkMode 
                    ? 'bg-violet-600/20 text-violet-400 border border-violet-500/30 shadow-neon-violet'
                    : 'bg-violet-100 text-violet-700 border border-violet-300'
                  : isDarkMode
                    ? 'text-zinc-400 hover:bg-white/5 hover:text-white'
                    : 'text-slate-600 hover:bg-slate-200 hover:text-slate-900'
              }`}
            >
              <Target className="w-4 h-4" />
              Security Initiatives
            </button>

            {/* Reporting */}
            <button
              data-testid="nav-reporting"
              onClick={() => navigate('/reporting')}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg font-inter text-xs transition-all ${
                isActive('/reporting') || isActive('/report-builder')
                  ? isDarkMode 
                    ? 'bg-indigo-600/20 text-indigo-400 border border-indigo-500/30 shadow-neon-indigo'
                    : 'bg-indigo-100 text-indigo-700 border border-indigo-300'
                  : isDarkMode
                    ? 'text-zinc-400 hover:bg-white/5 hover:text-white'
                    : 'text-slate-600 hover:bg-slate-200 hover:text-slate-900'
              }`}
            >
              <FileText className="w-4 h-4" />
              Reporting
            </button>

            {/* ITSM Orchestration with sub-menu */}
            <div>
              <button
                onClick={() => setItsmExpanded(!itsmExpanded)}
                className={`w-full flex items-center justify-between gap-2.5 px-3 py-2 rounded-lg font-inter text-xs transition-all ${
                  isActive('/itsm') || location.pathname.includes('cmdb')
                    ? isDarkMode 
                      ? 'bg-emerald-600/20 text-emerald-400 border border-emerald-500/30'
                      : 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                    : isDarkMode
                      ? 'text-zinc-400 hover:bg-white/5 hover:text-white'
                      : 'text-slate-600 hover:bg-slate-200 hover:text-slate-900'
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <Webhook className="w-4 h-4" />
                  ITSM
                </div>
                {itsmExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              </button>
              {itsmExpanded && (
                <div className="ml-6 mt-1 space-y-1">
                  <button
                    data-testid="nav-itsm-tickets"
                    onClick={() => navigate('/itsm')}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg font-inter text-xs transition-all ${
                      isActive('/itsm')
                        ? isDarkMode ? 'text-emerald-400 bg-emerald-600/10' : 'text-emerald-700 bg-emerald-100'
                        : isDarkMode ? 'text-zinc-500 hover:text-zinc-300' : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <Link2 className="w-3 h-3" />
                    Ticketing Integration
                  </button>
                  <button
                    data-testid="nav-cmdb-grc"
                    onClick={() => navigate('/itsm?tab=cmdb')}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg font-inter text-xs transition-all ${
                      location.search.includes('tab=cmdb')
                        ? isDarkMode ? 'text-blue-400 bg-blue-600/10' : 'text-blue-700 bg-blue-100'
                        : isDarkMode ? 'text-zinc-500 hover:text-zinc-300' : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <Database className="w-3 h-3" />
                    CMDB/GRC Sync
                  </button>
                </div>
              )}
            </div>

            {/* Identity Exposure */}
            <button
              data-testid="nav-identity"
              onClick={() => navigate('/identity-exposure')}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg font-inter text-xs transition-all ${
                isActive('/identity-exposure')
                  ? isDarkMode 
                    ? 'bg-red-600/20 text-red-400 border border-red-500/30'
                    : 'bg-red-100 text-red-700 border border-red-300'
                  : isDarkMode
                    ? 'text-zinc-400 hover:bg-white/5 hover:text-white'
                    : 'text-slate-600 hover:bg-slate-200 hover:text-slate-900'
              }`}
            >
              <UserX className="w-4 h-4" />
              Identity Exposure
            </button>

            {/* Crown Jewels */}
            <button
              data-testid="nav-crown-jewels"
              onClick={() => navigate('/crown-jewels')}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg font-inter text-xs transition-all ${
                isActive('/crown-jewels')
                  ? isDarkMode 
                    ? 'bg-yellow-600/20 text-yellow-400 border border-yellow-500/30'
                    : 'bg-yellow-100 text-yellow-700 border border-yellow-300'
                  : isDarkMode
                    ? 'text-zinc-400 hover:bg-white/5 hover:text-white'
                    : 'text-slate-600 hover:bg-slate-200 hover:text-slate-900'
              }`}
            >
              <Crown className="w-4 h-4" />
              Crown Jewels
            </button>
          </nav>

          <div className={`p-3 border-t space-y-2 ${isDarkMode ? 'border-white/5' : 'border-slate-200'}`}>
            {isPlatformAdmin && (
              <>
                <button
                  data-testid="nav-platform-admin"
                  onClick={() => navigate('/platform-admin')}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg font-inter text-xs transition-all ${
                    location.pathname === '/platform-admin'
                      ? isDarkMode 
                        ? 'bg-violet-600/20 text-violet-400 border border-violet-500/30 shadow-neon-violet'
                        : 'bg-violet-100 text-violet-700 border border-violet-300'
                      : isDarkMode
                        ? 'text-violet-400 hover:bg-violet-500/10'
                        : 'text-violet-600 hover:bg-violet-100'
                  }`}
                >
                  <Building2 className="w-4 h-4" />
                  Platform Admin
                </button>
                <button
                  data-testid="nav-registrations"
                  onClick={() => navigate('/registrations')}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg font-inter text-xs transition-all ${
                    location.pathname === '/registrations'
                      ? isDarkMode 
                        ? 'bg-violet-600/20 text-violet-400 border border-violet-500/30 shadow-neon-violet'
                        : 'bg-violet-100 text-violet-700 border border-violet-300'
                      : isDarkMode
                        ? 'text-violet-400 hover:bg-violet-500/10'
                        : 'text-violet-600 hover:bg-violet-100'
                  }`}
                >
                  <UserPlus className="w-4 h-4" />
                  Registrations
                </button>
              </>
            )}
            
            {isAdmin && (
              <button
                data-testid="nav-administration"
                onClick={() => navigate('/administration')}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg font-inter text-xs transition-all ${
                  location.pathname === '/administration'
                    ? isDarkMode 
                      ? 'bg-indigo-600/20 text-indigo-400 border border-indigo-500/30 shadow-neon-indigo'
                      : 'bg-indigo-100 text-indigo-700 border border-indigo-300'
                    : isDarkMode
                      ? 'text-zinc-400 hover:bg-white/5 hover:text-white'
                      : 'text-slate-600 hover:bg-slate-200 hover:text-slate-900'
                }`}
              >
                <Settings className="w-4 h-4" />
                Administration
              </button>
            )}
          </div>
        </div>
      </aside>

      <div className={`flex-1 transition-all duration-300 ${sidebarOpen && !isMobile ? 'lg:ml-64' : 'ml-0'} relative z-10`}>
        <header className={`sticky top-0 z-40 px-3 sm:px-4 lg:px-5 py-2 lg:py-2.5 ${isDarkMode ? 'glass-header' : 'glass-header'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 lg:gap-4">
              <button
                data-testid="sidebar-toggle"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className={`p-1.5 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-slate-200'}`}
              >
                <Menu className={`w-4 h-4 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`} />
              </button>
              
              {/* Tenant/Organization Display - hidden on mobile */}
              {currentOrg && (
                <div className={`hidden md:flex items-center gap-2 px-2 lg:px-3 py-1.5 glass rounded-lg`}>
                  <Building2 className={`w-3 lg:w-4 h-3 lg:h-4 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
                  <span className={`text-[10px] lg:text-xs font-inter truncate max-w-[100px] lg:max-w-none ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>{currentOrg.name}</span>
                  {isPlatformAdmin && tenants.length > 1 && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className={`ml-1 p-1 rounded ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-slate-200'}`}>
                          <ChevronDown className={`w-3 h-3 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className={`${isDarkMode ? 'bg-zinc-900/90 backdrop-blur-xl border-white/10 text-white' : 'bg-white/90 backdrop-blur-xl border-slate-200 text-slate-900'}`}>
                        <div className={`px-2 py-1.5 text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Switch Tenant</div>
                        <DropdownMenuSeparator className={isDarkMode ? 'bg-white/10' : 'bg-slate-200'} />
                        {tenants.map(tenant => (
                          <DropdownMenuItem 
                            key={tenant.id}
                            className={`cursor-pointer text-xs ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}
                          >
                            <Building2 className="w-3 h-3 mr-2" />
                            {tenant.name}
                            <span className={`ml-auto ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>{tenant.stats?.users || 0} users</span>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 lg:gap-3">
              {/* Theme Toggle Button - Professional Animated Toggle */}
              <button
                data-testid="theme-toggle"
                onClick={toggleTheme}
                className={`relative p-2 rounded-xl transition-all duration-300 group ${
                  isDarkMode 
                    ? 'bg-navy-800/60 hover:bg-navy-700/60 border border-navy-700 hover:border-electric-500/50 hover:shadow-neon-electric' 
                    : 'bg-white hover:bg-slate-50 border border-slate-200 hover:border-electric-500 shadow-card hover:shadow-card-hover'
                }`}
                title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              >
                <div className="relative w-4 h-4">
                  {/* Sun icon for dark mode */}
                  <Sun className={`absolute inset-0 w-4 h-4 transition-all duration-300 ${
                    isDarkMode 
                      ? 'opacity-100 rotate-0 text-yellow-400' 
                      : 'opacity-0 rotate-90 text-yellow-500'
                  }`} />
                  {/* Moon icon for light mode */}
                  <Moon className={`absolute inset-0 w-4 h-4 transition-all duration-300 ${
                    isDarkMode 
                      ? 'opacity-0 -rotate-90 text-slate-400' 
                      : 'opacity-100 rotate-0 text-electric-600'
                  }`} />
                </div>
              </button>

              {isPlatformAdmin && (
                <div className={`hidden sm:flex items-center gap-2 px-2 lg:px-3 py-1.5 rounded-lg border ${
                  isDarkMode 
                    ? 'bg-gradient-to-r from-violet-600/20 to-purple-600/20 border-violet-500/30 shadow-neon-violet'
                    : 'bg-gradient-to-r from-violet-100 to-purple-100 border-violet-300'
                }`}>
                  <Users className={`w-3 lg:w-4 h-3 lg:h-4 ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`} />
                  <span className={`text-[10px] lg:text-xs font-inter ${isDarkMode ? 'text-violet-300' : 'text-violet-700'}`}>{tenants.length} Tenants</span>
                </div>
              )}
              
              {/* User Profile Dropdown - Top Right */}
              <DropdownMenu open={profileDropdownOpen} onOpenChange={setProfileDropdownOpen}>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="user-profile-dropdown"
                    className={`flex items-center gap-2 px-2 lg:px-3 py-1.5 rounded-xl transition-all duration-300 ${
                      isDarkMode 
                        ? 'bg-navy-800/60 hover:bg-navy-700/60 border border-navy-700 hover:border-electric-500/50' 
                        : 'bg-white hover:bg-slate-50 border border-slate-200 hover:border-electric-500 shadow-card'
                    }`}
                  >
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${isDarkMode ? 'bg-indigo-600/30' : 'bg-indigo-100'}`}>
                      <User className={`w-3.5 h-3.5 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
                    </div>
                    <div className="hidden sm:block text-left">
                      <p className={`font-inter text-xs truncate max-w-[100px] ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>{user?.full_name}</p>
                      <p className={`text-[10px] truncate ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>{user?.role?.replace('_', ' ')}</p>
                    </div>
                    <ChevronDown className={`w-3 h-3 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent 
                  align="end" 
                  className={`w-56 ${isDarkMode ? 'bg-navy-900/98 backdrop-blur-xl border-navy-700 text-white' : 'bg-white/98 backdrop-blur-xl border-slate-200 text-slate-900'}`}
                >
                  <div className={`px-3 py-2 border-b ${isDarkMode ? 'border-navy-700' : 'border-slate-200'}`}>
                    <p className={`font-inter text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{user?.full_name}</p>
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>{user?.email}</p>
                  </div>
                  <DropdownMenuItem 
                    data-testid="nav-mfa-settings"
                    onClick={() => navigate('/settings/mfa')}
                    className={`cursor-pointer text-xs ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}
                  >
                    <Shield className="w-3.5 h-3.5 mr-2" />
                    MFA Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    data-testid="nav-change-password"
                    onClick={() => {
                      setProfileDropdownOpen(false);
                      setChangePasswordOpen(true);
                    }}
                    className={`cursor-pointer text-xs ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}
                  >
                    <KeyRound className="w-3.5 h-3.5 mr-2" />
                    Change Password
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    data-testid="nav-notification-settings"
                    onClick={() => navigate('/settings/notifications')}
                    className={`cursor-pointer text-xs ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}
                  >
                    <Bell className="w-3.5 h-3.5 mr-2" />
                    Notification Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    data-testid="nav-audit-logs"
                    onClick={() => navigate('/audit-logs')}
                    className={`cursor-pointer text-xs ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}
                  >
                    <FileCheck className="w-3.5 h-3.5 mr-2" />
                    Audit Logs
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className={isDarkMode ? 'bg-navy-700' : 'bg-slate-200'} />
                  <DropdownMenuItem 
                    data-testid="logout-button"
                    onClick={onLogout}
                    className={`cursor-pointer text-xs ${isDarkMode ? 'text-red-400 hover:bg-red-500/10' : 'text-red-600 hover:bg-red-100'}`}
                  >
                    <LogOut className="w-3.5 h-3.5 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <main className="p-3 sm:p-4 lg:p-5 overflow-x-auto">{children}</main>
      </div>
      
      {/* Change Password Dialog */}
      <Dialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen}>
        <DialogContent className={`max-w-md ${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white border-slate-200'}`}>
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              <KeyRound className={`w-5 h-5 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
              Change Password
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Current Password */}
            <div>
              <Label className={`text-sm mb-2 block ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>Current Password</Label>
              <div className="relative">
                <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`} />
                <Input
                  type={showPasswords.current ? "text" : "password"}
                  value={passwordData.current}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, current: e.target.value }))}
                  placeholder="Enter current password"
                  className={`pl-10 pr-10 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                  className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-zinc-500 hover:text-zinc-300' : 'text-slate-400 hover:text-slate-600'}`}
                >
                  {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            {/* New Password */}
            <div>
              <Label className={`text-sm mb-2 block ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>New Password</Label>
              <div className="relative">
                <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`} />
                <Input
                  type={showPasswords.new ? "text" : "password"}
                  value={passwordData.new}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, new: e.target.value }))}
                  placeholder="Enter new password"
                  className={`pl-10 pr-10 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                  className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-zinc-500 hover:text-zinc-300' : 'text-slate-400 hover:text-slate-600'}`}
                >
                  {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            {/* Confirm New Password */}
            <div>
              <Label className={`text-sm mb-2 block ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>Confirm New Password</Label>
              <div className="relative">
                <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`} />
                <Input
                  type={showPasswords.confirm ? "text" : "password"}
                  value={passwordData.confirm}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, confirm: e.target.value }))}
                  placeholder="Confirm new password"
                  className={`pl-10 pr-10 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                  className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-zinc-500 hover:text-zinc-300' : 'text-slate-400 hover:text-slate-600'}`}
                >
                  {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            {passwordData.new && passwordData.confirm && passwordData.new !== passwordData.confirm && (
              <p className="text-xs text-red-400">Passwords do not match</p>
            )}
            
            <div className={`p-3 rounded-lg text-xs ${isDarkMode ? 'bg-indigo-500/10 border border-indigo-500/20 text-indigo-400' : 'bg-indigo-50 border border-indigo-200 text-indigo-700'}`}>
              Password must be at least 8 characters long
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setChangePasswordOpen(false);
                setPasswordData({ current: '', new: '', confirm: '' });
              }}
              className={isDarkMode ? 'text-zinc-400 hover:text-white' : ''}
            >
              Cancel
            </Button>
            <Button
              onClick={handleChangePassword}
              disabled={changingPassword || !passwordData.current || !passwordData.new || !passwordData.confirm || passwordData.new !== passwordData.confirm}
              className="bg-indigo-600 hover:bg-indigo-500"
              data-testid="submit-change-password-btn"
            >
              {changingPassword ? 'Changing...' : 'Change Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
